import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import {
  fetchInstallerLimosa,
  openLimosaDocument,
  reviewLimosa,
  setLimosaProjects,
  type AdministratieRow,
  type LimosaDetail,
  type LimosaPerson,
  type LimosaProject,
} from '@/api/administratie';
import { formatDate } from './LimosaBadge';
import { VerplichtKeuze } from './VerplichtKeuze';
import { AdminLimosaUpload } from './AdminLimosaUpload';
import { LimosaProjects } from './LimosaProjects';
import type { LimosaVerplichtBron } from '@/api/administratie';

const STATUS_LABEL: Record<LimosaDetail['status'], string> = {
  te_controleren: 'Te controleren',
  goedgekeurd: 'Goedgekeurd',
  afgekeurd: 'Afgekeurd',
};

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError && err.message ? err.message : fallback);

/** A record's linked projects, saved on every change; on failure the previous list comes back. */
function RecordProjects({ recordId, initial, onSaved }: { recordId: number; initial: LimosaProject[]; onSaved: () => void }) {
  const [projects, setProjects] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = async (next: LimosaProject[]) => {
    const before = projects;
    setProjects(next);
    setBusy(true);
    setError(null);
    try {
      await setLimosaProjects(recordId, next.map((p) => p.id));
      onSaved();
    } catch (err) {
      setProjects(before);
      setError(errorText(err, 'Projecten bijwerken mislukt'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <LimosaProjects value={projects} onChange={change} disabled={busy} />
      {error && <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}

function RecordItem({ record, projects, onReviewed }: { record: LimosaDetail; projects: LimosaProject[]; onReviewed: () => void }) {
  const [van, setVan] = useState(record.geldigVan);
  const [tot, setTot] = useState(record.geldigTot);
  // Starts with the stored note (e.g. the PDF check's), so emptying the field clears it.
  const [opmerking, setOpmerking] = useState(record.opmerking ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = record.status === 'te_controleren';
  // An approved one, also one the PDF check approved, stays correctable and rejectable.
  const editable = pending || record.status === 'goedgekeurd';
  const changed = van !== record.geldigVan || tot !== record.geldigTot || opmerking.trim() !== (record.opmerking ?? '');

  const review = async (status: 'goedgekeurd' | 'afgekeurd') => {
    setBusy(true);
    setError(null);
    try {
      await reviewLimosa(record.id, {
        status,
        ...(opmerking.trim() !== (record.opmerking ?? '') ? { opmerking: opmerking.trim() } : {}),
        ...(van !== record.geldigVan ? { geldigVan: van } : {}),
        ...(tot !== record.geldigTot ? { geldigTot: tot } : {}),
      });
      onReviewed();
    } catch (err) {
      setError(errorText(err, 'Limosa bijwerken mislukt'));
    } finally {
      // An approved record stays in the list after saving, so it must be usable again.
      setBusy(false);
    }
  };

  const open = () => openLimosaDocument(record.id).catch((err) => setError(errorText(err, 'Document openen mislukt')));

  return (
    <li className="border border-gray-200 rounded-lg p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-td-navy">{record.partnerName}</span>
        <span className="text-xs font-semibold text-td-muted">{STATUS_LABEL[record.status]}</span>
      </div>
      {editable ? (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label className="text-xs text-td-muted">
            Geldig van
            <input type="date" value={van} onChange={(e) => setVan(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-td-muted">
            Geldig tot
            <input type="date" value={tot} onChange={(e) => setTot(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
        </div>
      ) : (
        <p className="mt-1 text-td-muted">
          Geldig van {formatDate(record.geldigVan)} tot {formatDate(record.geldigTot)}
        </p>
      )}
      {record.limosaNummer && <p className="text-xs text-td-muted mt-1">Declaratienummer: {record.limosaNummer}</p>}
      {record.uploadedBy && <p className="text-xs text-td-light mt-1">Opgeladen door {record.uploadedBy}</p>}
      {record.autoApproved
        ? <p className="text-xs text-td-light">Automatisch goedgekeurd (PDF-controle)</p>
        : record.reviewedBy && <p className="text-xs text-td-light">Gecontroleerd door {record.reviewedBy}</p>}
      {record.opmerking && <p className="text-xs text-td-muted mt-1">Opmerking: {record.opmerking}</p>}
      <RecordProjects recordId={record.id} initial={projects} onSaved={onReviewed} />

      <div className="mt-2 flex flex-wrap gap-2">
        {record.hasDocument && (
          <button type="button" onClick={open} className="px-3 py-1.5 text-xs font-semibold text-td-blue border border-td-blue rounded-lg hover:bg-td-blue/10">
            Document openen
          </button>
        )}
        {editable && (
          <>
            {pending ? (
              <button type="button" disabled={busy} onClick={() => review('goedgekeurd')} className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg disabled:opacity-60">
                {busy ? 'Bezig…' : 'Goedkeuren'}
              </button>
            ) : (
              <button type="button" disabled={busy || !changed} onClick={() => review('goedgekeurd')} className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg disabled:opacity-60">
                {busy ? 'Bezig…' : 'Wijzigingen opslaan'}
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => review('afgekeurd')} className="px-3 py-1.5 text-xs font-semibold text-red-700 border border-red-300 rounded-lg disabled:opacity-60">
              Afkeuren
            </button>
          </>
        )}
      </div>
      {editable && (
        <label className="block mt-2 text-xs text-td-muted">
          Opmerking (optioneel)
          <input type="text" maxLength={500} value={opmerking} onChange={(e) => setOpmerking(e.target.value)} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" />
        </label>
      )}
      {error && <p className="mt-2 text-xs text-red-600" role="alert">{error}</p>}
    </li>
  );
}

/** The worker a dialog was opened for, from that worker's own row. */
export interface LimosaDialogPerson {
  id: number;
  name: string;
}

/**
 * Limosa for one installer row: "verplicht" choice, declarations, document,
 * confirm / reject, upload. Opened from a worker's row (`person`), it is about
 * that worker only: the title names them and an upload is always for them.
 */
export function LimosaDialog({ row, person, onClose, onChanged }: {
  row: AdministratieRow;
  person?: LimosaDialogPerson;
  onClose: () => void;
  onChanged: () => void;
}) {
  const installerId = row.installerId!;
  const [people, setPeople] = useState<LimosaPerson[]>([]);
  const [records, setRecords] = useState<LimosaDetail[] | null>(null);
  const [verplicht, setVerplicht] = useState<Record<string, LimosaVerplichtBron> | null>(null);
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  const rowProjects: LimosaProject[] = row.projectId != null ? [{ id: row.projectId, name: row.projectName }] : [];
  const projectsOf = (r: LimosaDetail): LimosaProject[] =>
    r.projectIds.map((id) => ({ id, name: projectNames[String(id)] ?? `Project ${id}` }));
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // The people of this row: its workers, or the installer itself when it has none.
  const roster = people.some((p) => !p.isSelf) ? people.filter((p) => !p.isSelf) : people;
  const persons = person ? roster.filter((p) => p.id === person.id) : roster;
  const shownRecords = person ? records?.filter((r) => r.partnerId === person.id) ?? null : records;

  const load = useCallback(() => {
    fetchInstallerLimosa(installerId)
      .then((d) => {
        setPeople(d.people);
        setRecords(d.records);
        setVerplicht(d.verplicht ?? {});
        setProjectNames(d.projects ?? {});
      })
      .catch((err) => setError(errorText(err, 'Limosa ophalen mislukt')));
  }, [installerId]);

  useEffect(load, [load]);

  const reviewed = () => {
    load();
    onChanged();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="limosa-dialog-title"
        className="bg-white w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="limosa-dialog-title" className="text-lg font-extrabold text-td-navy">
            Limosa — {person ? person.name : row.installerName ?? 'Installateur'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="text-td-muted hover:text-td-navy text-xl leading-none">×</button>
        </div>

        {row.limosa == null && (
          <p className="mt-3 text-sm text-orange-700" role="status">
            De Limosa-status kon niet gelezen worden. Je kan hieronder nakijken en opladen; herlaad de lijst later voor de status.
          </p>
        )}

        {/* Decision 17: "Limosa verplicht" per person, by that person's own company. */}
        <section className="mt-4" aria-labelledby="limosa-verplicht-title">
          <h3 id="limosa-verplicht-title" className="text-sm font-semibold text-td-navy">Limosa verplicht</h3>
          {verplicht === null
            ? <p className="mt-1 text-sm text-td-muted">Laden…</p>
            : (
              <ul className="mt-1 divide-y divide-gray-100">
                {persons.map((p) => (
                  <VerplichtKeuze key={p.id} personId={p.id} name={p.name} bron={verplicht[String(p.id)] ?? null} onChanged={onChanged} />
                ))}
              </ul>
            )}
        </section>

        {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}

        <div className="mt-5 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-td-navy">Opgeladen Limosa&apos;s</h3>
          {!uploading && persons.length > 0 && (
            <button type="button" onClick={() => setUploading(true)} className="px-3 py-1.5 text-xs font-semibold text-td-blue border border-td-blue rounded-lg hover:bg-td-blue/10">
              Uploaden
            </button>
          )}
        </div>
        {uploading && (
          <AdminLimosaUpload
            installerId={installerId}
            people={persons}
            initialProjects={rowProjects}
            onCancel={() => setUploading(false)}
            onUploaded={() => {
              setUploading(false);
              reviewed();
            }}
          />
        )}
        {shownRecords === null && !error && <p className="mt-2 text-sm text-td-muted">Laden…</p>}
        {shownRecords?.length === 0 && <p className="mt-2 text-sm text-td-muted">Nog geen Limosa opgeladen.</p>}
        {shownRecords && shownRecords.length > 0 && (
          <ul className="mt-2 space-y-2">
            {shownRecords.map((r) => <RecordItem key={r.id} record={r} projects={projectsOf(r)} onReviewed={reviewed} />)}
          </ul>
        )}
      </div>
    </div>
  );
}
